import * as Yup from "yup"
import moment from "moment"
import _ from "lodash"

import { validationUtils } from "@oj-x/ui"

import localizationHelpers from "services/localize/helpers"
import { formatDateWithUserLocale, isInRange, getDateAsMoment } from "helpers/time"

/**
 * Customising yup date to format based on user's browser locale
 */
Yup.addMethod(Yup.date, "format", function format(formats) {
  return this.transform((value, originalValue) => {
    if (originalValue === "") {
      return undefined
    }

    const defaultFormat = localizationHelpers.getLocalizedCurrentDate()

    const convertedValue = moment(originalValue, formats || defaultFormat, true)
    return convertedValue.isValid() ? convertedValue.toDate() : new Date("")
  })
})

/**
 * Adding custom validation to yup date to check if a date is between 2 other dates
 * based on a minOffset and a maxOffset in year. These offsets are used to construct a min/max dates
 * based on a reference field or todays date (if not provided)
 * The reference field is a string (eg. "referenceDate" is the hotel start date -> see date of birth for customer)
 * if empty, it will consider today's date
 */
Yup.addMethod(
  Yup.date,
  "betweenPast",
  function betweenPast(minOffset, maxOffset, errorMessage, referenceField, options = {}) {
    const defaultUnit = "years"
    const { minOffsetUnit, maxOffsetUnit } = options

    return this.test("isBetweenPastDates", errorMessage, function test(value) {
      if (!value) {
        return true
      }
      const referenceDate = referenceField && _.get(this.options.context, referenceField)

      const minDate = validationUtils
        .makePastDate(referenceDate, maxOffset + 1, maxOffsetUnit || defaultUnit)
        .add(1, "days")
      const maxDate = validationUtils.makePastDate(
        referenceDate,
        minOffset,
        minOffsetUnit || defaultUnit
      )
      return isInRange(value, minDate, maxDate)
    })
  }
)

/**
 * Adding custom validation to yup date to check if a date is a future date base on a refence value between 2 other dates
 * The reference field is a string (eg. "referenceDate" is the flight start date -> see passport expired date)
 * if empty, it will consider today's date
 */
Yup.addMethod(Yup.date, "futureDate", function futureDate(errorMessage, referenceField) {
  return this.test("isFutureDate", errorMessage, function test(value) {
    if (!value) {
      return true
    }
    const referenceDate = referenceField && _.get(this.options.context, referenceField)
    return getDateAsMoment(value) > getDateAsMoment(referenceDate)
  })
})

Yup.addMethod(Yup.date, "pastDate", function pastDate(errorMessage, referenceField) {
  return this.test("isPastDate", errorMessage, function test(value) {
    if (!value) {
      return true
    }
    const referenceDate = referenceField
      ? getDateAsMoment(_.get(this.options.context, referenceField))
      : moment()
    return getDateAsMoment(value) <= referenceDate
  })
})

/**
 * Adding custom validation to yup date to check if a date is between 2 other dates
 * based on a minOffset and a maxOffset. These offsets are used to construct a min/max dates
 * based on a reference field or todays date (if not provided)
 * The reference field can either be a string (eg. "departureDate" -> see return flight schema)
 * or a function (eg. index => `bounds[${index - 1}].departureDate` -> see multicity schema)
 */
Yup.addMethod(
  Yup.date,
  "betweenFuture",
  function betweenFuture(minOffset, maxOffset, errorMessage, referenceField) {
    return this.test("isBetweenFutureDates", errorMessage, function test(value) {
      let referenceDate
      let minimumOffset

      if (typeof referenceField === "string") {
        referenceDate = formatDateWithUserLocale(this.parent[referenceField])
      } else if (typeof referenceField === "function") {
        // this.path is the current field being validated (eg. bounds[1].departureDate)
        // we're using the regex to get the current index then we will use that to access the previous item in the array
        const index = this.path.matchAll(/.*\[(\d+)\]\..*/gm).next().value[1]

        // we are using the context to access to the full tree of values being validate
        // with that we can validate against previous element of the array see https://github.com/jquense/yup/issues/165
        referenceDate = formatDateWithUserLocale(_.get(this.options.context, referenceField(index)))
      }

      if (typeof minOffset === "number") {
        minimumOffset = minOffset
      } else if (typeof minOffset === "function") {
        // this.path is the current field being validated (eg. bounds[1].departureDate)
        // we're using the regex to get the current index then we will use that to access the previous item in the array
        const index = this.path.matchAll(/.*\[(\d+)\]\..*/gm).next().value[1]

        minimumOffset = minOffset(index)
      }
      const minDate = validationUtils.makeFutureDateInRange(referenceDate, minimumOffset)
      const maxDate = validationUtils.makeFutureDateInRange(referenceDate, maxOffset)
      return isInRange(value, minDate, maxDate)
    })
  }
)

/**
 * Adding custom validation to yup string to check if a string is different from another dependent string
 * eg. to make sure the departure location is different from the origin location
 */
Yup.addMethod(Yup.string, "differentFrom", function differentFrom(errorMessage, referenceField) {
  return this.test("isDifferentFrom", errorMessage, function hasDifferentValues(value) {
    return this.parent[referenceField] !== value
  })
})

/**
 * Customising yup object to transform an array into an object
 */
Yup.addMethod(Yup.object, "castArray", function castArray() {
  return this.transform((value, originalValue) => {
    if (Array.isArray(originalValue)) {
      return { ...originalValue }
    }
    return value
  })
})
